import React, {
    FC, useCallback, useMemo, useState,
} from 'react';

import { Button, Dropdown } from 'antd';
import { CaretDownOutlined, CheckOutlined, TeamOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import EditableCellPopup from '@/components/Common/EditableCellPopup';
import RevocationPopover, { type RevocationType } from './RevocationPopover';
import useRecipientDetails from '@/components/Common/hooks/useRecipientDetails';
import usePublicDomainCheck from '@/components/Common/hooks/usePublicDomainCheck';
import { useStores } from '@/components/hooks';
import { ForwardingStatus } from '@/components/FilesTable/Modals/RevocationWarningModal/helpers';
import { SharedUserToDisplay } from '@/stores';
import { RecipientRoles } from '@/types/types';
import styles from './PermissionDrawer.module.scss';

const nameSpace = 'filesTable.permissionDrawer';
const rolesNameSpace = 'sharingBlock.recipientRolesSelect';
const ALL_ROLES: RecipientRoles[] = ['viewer', 'editor', 'coOwner'];
const PHONE_LAST_DIGITS = -2;

export interface RecipientRowProps {
    user: SharedUserToDisplay;
    allUsers: SharedUserToDisplay[];
    showPhoneColumn: boolean;
    showPasswordColumn: boolean;
    isEditable: boolean;
    isUpdating: boolean;
    usersPhonesMap: Map<string, { phonesVector: { currentPhone: { phone?: string; prefix?: string } | null } }>;
    forwardingStatus: ForwardingStatus;
    onRoleChange: (email: string, role: RecipientRoles, user: SharedUserToDisplay) => void;
    onRevoke: (email: string, user: SharedUserToDisplay) => void;
    onRevokeAllDomain: (email: string) => void;
    /** First data row: same test id as ShareSettingsTable revoke column (e2e). */
    isFirstRecipientRow?: boolean;
}

const RecipientRow: FC<RecipientRowProps> = observer(({
    user,
    allUsers,
    showPhoneColumn,
    showPasswordColumn,
    isEditable,
    isUpdating,
    usersPhonesMap,
    forwardingStatus,
    onRoleChange,
    onRevoke,
    onRevokeAllDomain,
    isFirstRecipientRow = false,
}) => {
    const { t } = useTranslation();
    const { usersPhonesStore } = useStores();
    const { email, recipientRole: role, deleted: isDeleted } = user;
    const isGroup = user.recipientType === 'group';

    const [revokePopoverOpen, setRevokePopoverOpen] = useState(false);

    const {
        passwordInfo, onPhoneSubmit, onPasswordSave,
    } = useRecipientDetails({
        email,
        showPhones: showPhoneColumn,
        showPasswords: showPasswordColumn,
    });

    const phoneInfo = usersPhonesMap.get(email)?.phonesVector?.currentPhone;
    const hasPhone = !!(phoneInfo?.phone && phoneInfo?.prefix);
    const hasPassword = !!passwordInfo?.hasPassword;

    const { isPublicDomain } = usePublicDomainCheck(email);

    const revocationType: RevocationType = useMemo(() => {
        if (isPublicDomain) return 'simple';
        if (forwardingStatus.isForwardingEnabled || (forwardingStatus.isDomainForwardingEnabled && isGroup)) {
            return 'forwarding';
        }
        if (forwardingStatus.isDomainForwardingEnabled) {
            return 'domainForwarding';
        }
        return 'simple';
    }, [forwardingStatus, isGroup, isPublicDomain]);

    const domainUsers = useMemo(() => {
        if (revocationType !== 'domainForwarding') return [];
        const userDomain = email.split('@')[1]?.toLowerCase();
        if (!userDomain) return [];
        return allUsers
            .filter((u) => {
                const d = u.email.split('@')[1]?.toLowerCase();
                return d === userDomain && !u.deleted && u.email !== email;
            })
            .map((u) => u.email);
    }, [revocationType, email, allUsers]);

    const handleRevokeMenuClick = useCallback((): void => {
        setRevokePopoverOpen(true);
    }, []);

    const handleRevokeCancel = useCallback((): void => {
        setRevokePopoverOpen(false);
    }, []);

    const handleRevokeContinue = useCallback((): void => {
        setRevokePopoverOpen(false);
        onRevoke(email, user);
    }, [email, user, onRevoke]);

    const handleRevokeFromDomain = useCallback((): void => {
        setRevokePopoverOpen(false);
        onRevokeAllDomain(email);
    }, [email, onRevokeAllDomain]);

    const roleDropdown = (
        <Dropdown
            trigger={['click']}
            disabled={isUpdating || revokePopoverOpen}
            rootClassName={styles['role-dropdown']}
            menu={{
                onClick: ({ key }) => {
                    if (key === 'revoke') {
                        handleRevokeMenuClick();
                    } else {
                        onRoleChange(email, key as RecipientRoles, user);
                    }
                },
                items: [
                    ...ALL_ROLES.map((r) => ({
                        key: r,
                        className: classNames(styles['role-menu-item'], {
                            [styles['chosen-item']]: r === role,
                        }),
                        label: (
                            <span className={styles['role-option']}>
                                {t(`${rolesNameSpace}.${r}.title`)}
                                {r === role && <CheckOutlined className={styles['check-icon']} />}
                            </span>
                        ),
                    })),
                    { type: 'divider' as const },
                    {
                        key: 'revoke',
                        className: styles['revoke-item'],
                        label: t(`${nameSpace}.revoke`),
                    },
                ],
            }}
        >
            <Button
                type="link"
                className={styles['role-dropdown-trigger']}
                {...(isFirstRecipientRow
                    ? { 'data-testid': 'manageAccess_revokeFirstUser' }
                    : { 'data-testid': `permissionDrawer_roleDropdown_${user.userId}` })}
            >
                <span className={styles['role-text']}>
                    {t(`${rolesNameSpace}.${role}.title`)}
                </span>
                <CaretDownOutlined />
            </Button>
        </Dropdown>
    );

    return (
        <div
            className={styles['recipient-row']}
            data-testid={`permissionDrawer_recipientRow_${user.userId}`}
        >
            <div className={styles['col-email']}>
                {isGroup ? (
                    <span
                        className={classNames(styles['recipient-email'], styles['recipient-email-group'])}
                        onClick={() => usersPhonesStore.setPhoneNumbersGroupId(user.userId)}
                    >
                        <TeamOutlined className={styles['group-icon']} />
                        {email}
                    </span>
                ) : (
                    <span
                        className={classNames(
                            styles['recipient-email'],
                            { [styles['deleted-email']]: isDeleted },
                        )}
                    >
                        {email}
                    </span>
                )}
            </div>

            {showPhoneColumn && (
                <div className={styles['col-phone']}>
                    {!isGroup && (
                        <EditableCellPopup
                            type="phone"
                            phone={phoneInfo?.phone}
                            prefix={phoneInfo?.prefix}
                            onSave={onPhoneSubmit}
                            isEmpty={!hasPhone}
                            cellTestId={`permissionDrawer_recipientPhone_${user.userId}`}
                            displayValue={
                                hasPhone
                                    ? (
                                        <span className={styles['detail-text']}>
                                            {`+${phoneInfo.prefix}*******`}
                                            {(phoneInfo.phone || '').slice(PHONE_LAST_DIGITS)}
                                        </span>
                                    )
                                    : <span className={styles['detail-text-empty']}>—</span>
                            }
                        />
                    )}
                </div>
            )}

            {showPasswordColumn && (
                <div className={styles['col-password']}>
                    {!isGroup && (
                        <EditableCellPopup
                            type="password"
                            maskedPassword={passwordInfo?.maskedPassword}
                            hasPassword={hasPassword}
                            onSave={onPasswordSave}
                            isEmpty={!hasPassword}
                            cellTestId={`permissionDrawer_recipientPassword_${user.userId}`}
                            displayValue={
                                hasPassword
                                    ? (
                                        <span className={styles['detail-text']}>
                                            {`•••••••••${(passwordInfo?.maskedPassword || '').slice(-1)}`}
                                        </span>
                                    )
                                    : <span className={styles['detail-text-empty']}>—</span>
                            }
                        />
                    )}
                </div>
            )}

            <div className={styles['col-role']}>
                {!isDeleted && isEditable ? (
                    <RevocationPopover
                        open={revokePopoverOpen}
                        onOpenChange={(open) => {
                            if (!open) setRevokePopoverOpen(false);
                        }}
                        type={revocationType}
                        userEmail={email}
                        isGroup={isGroup}
                        domainUsers={domainUsers}
                        onCancel={handleRevokeCancel}
                        onContinue={handleRevokeContinue}
                        onRevokeFromDomain={revocationType === 'domainForwarding' ? handleRevokeFromDomain : undefined}
                    >
                        {roleDropdown}
                    </RevocationPopover>
                ) : (
                    <span className={styles['role-text']}>
                        {t(`${rolesNameSpace}.${role}.title`)}
                    </span>
                )}
            </div>
        </div>
    );
});

export default RecipientRow;
